import type { LeadCloudFileType } from '@econtractor/types';

export type CloudFileUploadResult = { success: boolean; config: LeadCloudFileType };

export const uploadFileWithProgress = (
    file: File,
    cfg: LeadCloudFileType,
    onProgress?: (percent: number) => void,
): Promise<CloudFileUploadResult> =>
    new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', cfg.uploadUrl || '');
        const contentType = String(cfg?.contentType || file?.type || 'application/octet-stream');
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.addEventListener('progress', (event) => {
            if (!event.lengthComputable || !onProgress) return;
            onProgress((event.loaded / event.total) * 100);
        });

        xhr.addEventListener('load', () => {
            resolve({
                success: xhr.status >= 200 && xhr.status < 300,
                config: cfg,
            });
        });

        xhr.addEventListener('error', () => {
            resolve({
                success: false,
                config: cfg,
            });
        });

        xhr.send(file);
    });

export const uploadFilesWithOverallProgress = (
    files: File[],
    uploadConfigs: LeadCloudFileType[],
    onOverallProgress?: (percent: number) => void,
): Promise<CloudFileUploadResult[]> => {
    const fileSizes = files.map((file) => file.size || 1);
    const totalSize = fileSizes.reduce((sum, size) => sum + size, 0) || files.length;
    const fileProgresses = files.map(() => 0);
    const updateOverallProgress = () => {
        if (!onOverallProgress) return;
        const loaded = fileProgresses.reduce(
            (sum, percent, index) => sum + (percent / 100) * (fileSizes[index] || 1),
            0,
        );
        onOverallProgress(Math.min(100, Math.round((loaded / totalSize) * 100)));
    };

    return Promise.all(
        uploadConfigs.map((cfg, index) =>
            uploadFileWithProgress(files[index], cfg, (percent) => {
                fileProgresses[index] = percent;
                updateOverallProgress();
            }),
        ),
    );
};
